import {
  useListSociumIntegrations,
  useSociumFormationDetail,
  useSociumFormations,
  useSociumIntegrationStats,
  useSociumLearnerDetail,
  useSociumLearners,
  useSociumSessionDetail,
  useSociumSessions,
  SociumFormationRow,
  SociumLearnerRow,
  SociumSessionRow,
} from '@/api/hooks/useSocium';
import { Header } from '@/components/Layout/Header/Header';
import { Modal } from '@/components/Layout';
import { Tabs } from '@/components/Ui';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

const headerCol = 'text-primary bg-[#131311] text-sm font-bold';
const bodyCol = 'text-white bg-[#131311] border-b border-[#8080803d] text-sm';

const StatCard = ({ label, value }: { label: string; value: number }) => (
  <div className="bg-[#131311] border border-[#8080803d] rounded-[8px] px-4 py-3">
    <p className="text-primary text-2xl font-bold">{value}</p>
    <p className="text-[#939393] text-xs mt-1">{label}</p>
  </div>
);

const Pill = ({ ok, text }: { ok: boolean; text: string }) => (
  <span
    className={`px-2 py-1 rounded text-xs font-semibold ${
      ok ? 'bg-[#1E3A24] text-[#4ADE80]' : 'bg-[#3A2E12] text-[#FEC200]'
    }`}
  >
    {text}
  </span>
);

const TABS = [
  { id: 0, title: 'Cours' },
  { id: 1, title: 'Sessions' },
  { id: 2, title: 'Apprenants' },
];

export const SociumDetail = () => {
  const { integrationId } = useParams<{ integrationId: string }>();
  const [activeIndex, setActiveIndex] = useState(0);

  const { data: integrations } = useListSociumIntegrations();
  const integration = useMemo(
    () => integrations?.find((item) => item._id === integrationId),
    [integrations, integrationId],
  );

  const { data: stats } = useSociumIntegrationStats(integrationId);
  const { data: formations, isLoading: loadingFormations } =
    useSociumFormations(integrationId);
  const { data: sessions, isLoading: loadingSessions } =
    useSociumSessions(integrationId);
  const { data: learners, isLoading: loadingLearners } =
    useSociumLearners(integrationId);

  // Drill-down : type + ref sélectionnés au clic sur une ligne.
  const [drill, setDrill] = useState<{
    type: 'formation' | 'session' | 'learner';
    ref: string;
    label: string;
  } | null>(null);
  const formationRef = drill?.type === 'formation' ? drill.ref : undefined;
  const sessionRef = drill?.type === 'session' ? drill.ref : undefined;
  const learnerRef = drill?.type === 'learner' ? drill.ref : undefined;
  const { data: formationDetail, isLoading: loadingFormationDetail } =
    useSociumFormationDetail(integrationId, formationRef);
  const { data: sessionDetail, isLoading: loadingSessionDetail } =
    useSociumSessionDetail(integrationId, sessionRef);
  const { data: learnerDetail, isLoading: loadingLearnerDetail } =
    useSociumLearnerDetail(integrationId, learnerRef);

  const drillLoading =
    loadingFormationDetail || loadingSessionDetail || loadingLearnerDetail;

  return (
    <>
      <Header title={`Socium — ${integration?.name ?? '…'}`} goBack />

      <div className="px-6 py-4">
        {/* Bandeau stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
            <StatCard label="Cours créés" value={stats.formationsCount} />
            <StatCard label="Sessions" value={stats.sessionsCount} />
            <StatCard label="Modules publiés" value={stats.modulesCount} />
            <StatCard label="Apprenants inscrits" value={stats.learnersCount} />
            <StatCard
              label="Comptes provisionnés"
              value={stats.usersProvisioned}
            />
          </div>
        )}

        <Tabs
          tabs={TABS}
          activeIndex={activeIndex}
          setActiveIndex={setActiveIndex}
        />

        <div className="bg-[#131311] p-4 mt-4 rounded-[8px]">
          {activeIndex === 0 && (
            <DataTable
              value={formations ?? []}
              loading={loadingFormations}
              emptyMessage="Aucun cours."
              rowClassName={() => 'cursor-pointer'}
              onRowClick={(e) => {
                const row = e.data as SociumFormationRow;
                setDrill({
                  type: 'formation',
                  ref: row.externalRef,
                  label: row.title,
                });
              }}
            >
              <Column
                field="title"
                header="Cours"
                headerClassName={headerCol}
                bodyClassName={bodyCol}
              />
              <Column
                field="externalRef"
                header="Réf. Socium"
                headerClassName={headerCol}
                bodyClassName={bodyCol}
              />
              <Column
                header="Publié"
                headerClassName={headerCol}
                bodyClassName={bodyCol}
                body={(row: SociumFormationRow) => (
                  <Pill ok={row.isPublished} text={row.isPublished ? 'Oui' : 'Non'} />
                )}
              />
              <Column
                field="modulesCount"
                header="Modules"
                headerClassName={headerCol}
                bodyClassName={bodyCol}
              />
              <Column
                field="sessionsCount"
                header="Sessions"
                headerClassName={headerCol}
                bodyClassName={bodyCol}
              />
            </DataTable>
          )}

          {activeIndex === 1 && (
            <DataTable
              value={sessions ?? []}
              loading={loadingSessions}
              emptyMessage="Aucune session."
              rowClassName={() => 'cursor-pointer'}
              onRowClick={(e) => {
                const row = e.data as SociumSessionRow;
                setDrill({
                  type: 'session',
                  ref: row.externalRef,
                  label: row.name,
                });
              }}
            >
              <Column
                field="name"
                header="Session"
                headerClassName={headerCol}
                bodyClassName={bodyCol}
              />
              <Column
                header="Cours lié"
                headerClassName={headerCol}
                bodyClassName={bodyCol}
                body={(row: SociumSessionRow) => row.formationTitle ?? '—'}
              />
              <Column
                field="externalRef"
                header="Réf. Socium"
                headerClassName={headerCol}
                bodyClassName={bodyCol}
              />
              <Column
                field="participantsCount"
                header="Participants"
                headerClassName={headerCol}
                bodyClassName={bodyCol}
              />
            </DataTable>
          )}

          {activeIndex === 2 && (
            <DataTable
              value={learners ?? []}
              loading={loadingLearners}
              emptyMessage="Aucun compte."
              rowClassName={() => 'cursor-pointer'}
              onRowClick={(e) => {
                const row = e.data as SociumLearnerRow;
                setDrill({
                  type: 'learner',
                  ref: row.externalUserRef,
                  label:
                    `${row.firstName ?? ''} ${row.lastName ?? ''}`.trim() ||
                    row.externalUserRef,
                });
              }}
            >
              <Column
                header="Nom"
                headerClassName={headerCol}
                bodyClassName={bodyCol}
                body={(row: SociumLearnerRow) =>
                  `${row.firstName ?? ''} ${row.lastName ?? ''}`.trim() || '—'
                }
              />
              <Column
                header="Contact"
                headerClassName={headerCol}
                bodyClassName={bodyCol}
                body={(row: SociumLearnerRow) =>
                  row.email || row.phoneNumber || '—'
                }
              />
              <Column
                field="role"
                header="Rôle"
                headerClassName={headerCol}
                bodyClassName={bodyCol}
              />
              <Column
                field="classesCount"
                header="Classes"
                headerClassName={headerCol}
                bodyClassName={bodyCol}
              />
              <Column
                field="externalUserRef"
                header="Réf. Socium"
                headerClassName={headerCol}
                bodyClassName={bodyCol}
              />
            </DataTable>
          )}
        </div>
      </div>

      {/* Drill-down */}
      <Modal
        isOpen={!!drill}
        onClose={() => setDrill(null)}
        headerStyle="bg-carbon h-[36px]"
        modalStyle="bg-carbon"
      >
        <div className="flex flex-col gap-4 w-[640px] max-w-full p-2">
          <h3 className="text-white text-lg font-bold">{drill?.label}</h3>

          {drillLoading && (
            <p className="text-[#939393] text-sm py-6 text-center">
              Chargement…
            </p>
          )}

          {/* Cours → modules + sessions */}
          {drill?.type === 'formation' && formationDetail && (
            <div className="flex flex-col gap-4">
              <p className="text-sm text-[#939393]">
                {formationDetail.isPublished ? 'Publié' : 'Brouillon'} ·{' '}
                {formationDetail.modules.length} module(s) ·{' '}
                {formationDetail.sessions.length} session(s)
              </p>
              <div>
                <p className="text-primary text-sm font-bold mb-1">Modules</p>
                <div className="bg-[#131311] rounded-[8px]">
                  <DataTable
                    value={formationDetail.modules}
                    emptyMessage="Aucun module."
                  >
                    <Column
                      field="title"
                      header="Titre"
                      headerClassName={headerCol}
                      bodyClassName={bodyCol}
                    />
                  </DataTable>
                </div>
              </div>
              <div>
                <p className="text-primary text-sm font-bold mb-1">Sessions</p>
                <div className="bg-[#131311] rounded-[8px]">
                  <DataTable
                    value={formationDetail.sessions}
                    emptyMessage="Aucune session."
                  >
                    <Column
                      field="name"
                      header="Session"
                      headerClassName={headerCol}
                      bodyClassName={bodyCol}
                    />
                    <Column
                      field="participantsCount"
                      header="Participants"
                      headerClassName={headerCol}
                      bodyClassName={bodyCol}
                    />
                  </DataTable>
                </div>
              </div>
            </div>
          )}

          {/* Session → participants */}
          {drill?.type === 'session' && sessionDetail && (
            <div className="flex flex-col gap-3">
              <p className="text-sm text-[#939393]">
                Cours : {sessionDetail.formationTitle ?? '—'} ·{' '}
                {sessionDetail.participants.length} participant(s)
              </p>
              <div className="bg-[#131311] rounded-[8px]">
                <DataTable
                  value={sessionDetail.participants}
                  emptyMessage="Aucun participant."
                >
                  <Column
                    header="Nom"
                    headerClassName={headerCol}
                    bodyClassName={bodyCol}
                    body={(row) =>
                      `${row.firstName ?? ''} ${row.lastName ?? ''}`.trim() ||
                      '—'
                    }
                  />
                  <Column
                    header="Contact"
                    headerClassName={headerCol}
                    bodyClassName={bodyCol}
                    body={(row) => row.email || row.phoneNumber || '—'}
                  />
                  <Column
                    field="status"
                    header="Statut"
                    headerClassName={headerCol}
                    bodyClassName={bodyCol}
                  />
                </DataTable>
              </div>
            </div>
          )}

          {/* Apprenant → rattachements + progression */}
          {drill?.type === 'learner' && learnerDetail && (
            <div className="flex flex-col gap-3">
              <p className="text-sm text-[#939393]">
                {learnerDetail.email || learnerDetail.phoneNumber || '—'} ·{' '}
                {learnerDetail.role} · {learnerDetail.memberships.length}{' '}
                session(s)
              </p>
              <div className="bg-[#131311] rounded-[8px]">
                <DataTable
                  value={learnerDetail.memberships}
                  emptyMessage="Aucun rattachement."
                >
                  <Column
                    field="sessionName"
                    header="Session"
                    headerClassName={headerCol}
                    bodyClassName={bodyCol}
                  />
                  <Column
                    header="Cours"
                    headerClassName={headerCol}
                    bodyClassName={bodyCol}
                    body={(row) => row.formationTitle ?? '—'}
                  />
                  <Column
                    field="status"
                    header="Statut"
                    headerClassName={headerCol}
                    bodyClassName={bodyCol}
                  />
                  <Column
                    header="Progression"
                    headerClassName={headerCol}
                    bodyClassName={bodyCol}
                    body={(row) =>
                      `${row.progressPercent}% (${row.completedModules}/${row.totalModules})`
                    }
                  />
                </DataTable>
              </div>
            </div>
          )}

          <div className="flex justify-end pt-1">
            <button
              className="bg-[#2A2A2A] text-white text-sm px-5 py-2 rounded"
              onClick={() => setDrill(null)}
            >
              Fermer
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
};
